import { type FC, useContext, useEffect } from "react";

import { useQuery } from "@apollo/client";
import { useRecoilState, useSetRecoilState } from "recoil";

import { getOrganizations as getCamkitOrganizations } from "../../camkit/organizations/api/query";
import { type GetCameraKitOrganizationsQuery as GetCamkitOrganizationsQuery } from "../../camkit/organizations/api/query/getOrganizations.gql-generated";
import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { CofConfigId } from "../../gql-generated/types";
import { UserInfoContext } from "../../shared/api/contexts";
import {
    type GetOrganizationsQuery as GetLensCentralOrganizationsQuery,
    getOrganizations as getLensCentralOrganizations,
} from "../../shared/api/queries";

import {
    allOrganizationsState,
    camkitOrganizationsState,
    defaultOrganizationIdState,
    lensCentralOrganizationsState,
    organizationsInitializedState,
    organizationsLoadingState,
    organizationsRefetchState,
} from "./organizations";

export const OrganizationsStateInitializer: FC = () => {
    const { configs, loading: featureGatesLoading } = useFeatureGates();
    const setLensCentralOrganizations = useSetRecoilState(lensCentralOrganizationsState);
    const setCamkitOrganizations = useSetRecoilState(camkitOrganizationsState);
    const setAllOrganizations = useSetRecoilState(allOrganizationsState);
    const setDefaultOrganizationId = useSetRecoilState(defaultOrganizationIdState);
    const setLoading = useSetRecoilState(organizationsLoadingState);
    const [refetchCounter] = useRecoilState(organizationsRefetchState);
    const setInitialized = useSetRecoilState(organizationsInitializedState);

    const userInfo = useContext(UserInfoContext);

    const {
        data: lensCentralOrgsData,
        loading: lensCentralOrgsLoading,
        refetch: refetchLensCentral,
    } = useQuery<GetLensCentralOrganizationsQuery>(getLensCentralOrganizations);

    const {
        data: camkitOrgsData,
        loading: camkitOrgsLoading,
        refetch: refetchCamkit,
    } = useQuery<GetCamkitOrganizationsQuery>(getCamkitOrganizations, {
        variables: {
            includeAllOrgs: false,
        },
        skip: !configs[CofConfigId.MyLensesPortalUnification],
    });

    const {
        data: allOrgsData,
        loading: allOrgsLoading,
        refetch: refetchAll,
    } = useQuery<GetCamkitOrganizationsQuery>(getCamkitOrganizations, {
        variables: {
            includeAllOrgs: true,
        },
    });

    useEffect(() => {
        const refetchFn = async () => {
            if (refetchCounter > 0) {
                await refetchLensCentral();
                await refetchCamkit();
                await refetchAll();
            }
        };
        void refetchFn();
    }, [refetchCounter, refetchLensCentral, refetchCamkit, refetchAll]);

    const isLoading =
        lensCentralOrgsLoading ||
        camkitOrgsLoading ||
        allOrgsLoading ||
        featureGatesLoading ||
        !userInfo ||
        userInfo.loading;

    useEffect(() => {
        setLoading(isLoading);
        setInitialized(!isLoading);

        if (!isLoading) {
            setLensCentralOrganizations(lensCentralOrgsData);
            setCamkitOrganizations(camkitOrgsData);
            setAllOrganizations(allOrgsData);
            if (userInfo.data) {
                setDefaultOrganizationId(userInfo.data.getUserProfile.defaultOrganizationId);
            }
        }
    }, [
        isLoading,
        lensCentralOrgsData,
        camkitOrgsData,
        allOrgsData,
        userInfo,
        setLoading,
        setInitialized,
        setLensCentralOrganizations,
        setCamkitOrganizations,
        setAllOrganizations,
        setDefaultOrganizationId,
    ]);

    return null;
};
